import {
  useCallback,
  useState,
} from 'react';

export type FinancialMonthPeriod = {
  id: string | null;

  periodKey: string;

  periodStart:
    string | null;

  periodEnd:
    string | null;

  status:
    'open' | 'closed';

  closingBalanceCents?:
    number;

  accountCount?:
    number;

  lastAction?:
    string | null;

  lastActionAt?:
    string | null;

  lastActionBy?:
    string | null;

  pendingReconciliationCount?:
    number;

  pendingReconciliationAmountCents?:
    number;

  createdAt:
    string | null;

  updatedAt:
    string | null;
};

export type FinancialMonthEvent = {
  id: string;

  action:
    'close' | 'reopen';

  reason: string | null;

  createdByUserId:
    string | null;

  createdByName:
    string | null;

  createdAt: string;
};

export type FinancialMonthSnapshot = {
  id: string;

  accountId: string;
  accountName: string;

  balanceBeforeCents:
    number;

  settlementReceiptsCents:
    number;

  settlementReversalsCents:
    number;

  ledgerNetCents:
    number;

  closingBalanceCents:
    number;

  statementNetCents:
    number;

  statementCount:
    number;

  unreconciledCount:
    number;

  unreconciledAmountCents:
    number;

  createdAt: string;
};

export function useFinancialMonthClosing() {
  const [
    financialMonthPeriods,
    setFinancialMonthPeriods,
  ] = useState<
    FinancialMonthPeriod[]
  >([]);

  const [
    selectedFinancialMonth,
    setSelectedFinancialMonth,
  ] = useState<
    FinancialMonthPeriod | null
  >(null);

  const [
    financialMonthEvents,
    setFinancialMonthEvents,
  ] = useState<
    FinancialMonthEvent[]
  >([]);

  const [
    financialMonthSnapshots,
    setFinancialMonthSnapshots,
  ] = useState<
    FinancialMonthSnapshot[]
  >([]);

  const [
    isLoadingFinancialMonth,
    setIsLoadingFinancialMonth,
  ] = useState(false);

  const [
    financialMonthError,
    setFinancialMonthError,
  ] = useState<string | null>(
    null
  );

  const loadFinancialMonthPeriods =
    useCallback(async () => {
      try {
        const response =
          await fetch(
            '/api/financial-month-periods',
            {
              credentials:
                'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.periods
          )
        ) {
          throw new Error(
            data.error ||
              'Não foi possível carregar os fechamentos.'
          );
        }

        setFinancialMonthPeriods(
          data.periods
        );

        return (
          data.periods as
            FinancialMonthPeriod[]
        );
      } catch (error: any) {
        setFinancialMonthError(
          error?.message ||
            'Não foi possível carregar os fechamentos.'
        );

        return [];
      }
    }, []);

  const loadFinancialMonthDetail =
    useCallback(
      async (
        periodKey: string
      ) => {
        setIsLoadingFinancialMonth(
          true
        );

        setFinancialMonthError(
          null
        );

        try {
          const response =
            await fetch(
              `/api/financial-month-periods/${encodeURIComponent(periodKey)}`,
              {
                credentials:
                  'include',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !data.period
          ) {
            throw new Error(
              data.error ||
                'Não foi possível carregar a competência.'
            );
          }

          setSelectedFinancialMonth(
            data.period
          );

          setFinancialMonthEvents(
            Array.isArray(
              data.events
            )
              ? data.events
              : []
          );

          setFinancialMonthSnapshots(
            Array.isArray(
              data.snapshots
            )
              ? data.snapshots
              : []
          );

          return data;
        } catch (error: any) {
          setFinancialMonthError(
            error?.message ||
              'Não foi possível carregar a competência.'
          );

          return null;
        } finally {
          setIsLoadingFinancialMonth(
            false
          );
        }
      },
      []
    );

  const closeFinancialMonth =
    useCallback(
      async (
        periodKey: string,
        reason?: string | null
      ) => {
        const response =
          await fetch(
            '/api/financial-month-periods/close',
            {
              method: 'POST',

              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify({
                  periodKey,
                  reason:
                    reason || null,
                }),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          const error =
            new Error(
              data.error ||
                'Não foi possível fechar a competência.'
            ) as Error & {
              details?: any;
            };

          error.details =
            data;

          throw error;
        }

        return data;
      },
      []
    );

  const reopenFinancialMonth =
    useCallback(
      async (
        periodKey: string,
        reason: string
      ) => {
        const response =
          await fetch(
            `/api/financial-month-periods/${encodeURIComponent(periodKey)}/reopen`,
            {
              method: 'POST',

              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify({
                  reason,
                }),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              'Não foi possível reabrir a competência.'
          );
        }

        return data;
      },
      []
    );

  return {
    financialMonthPeriods,

    selectedFinancialMonth,

    financialMonthEvents,

    financialMonthSnapshots,

    isLoadingFinancialMonth,

    financialMonthError,

    loadFinancialMonthPeriods,

    loadFinancialMonthDetail,

    closeFinancialMonth,

    reopenFinancialMonth,
  };
}